import { getAbsoluteUrl } from "./urls";
import { fetchAuthenticated } from "./utils";

export type IvaDocumentType =
  | "invoice"
  | "credit_note"
  | "debit_note"
  | "received_document";

export interface IvaPeriodResult {
  taxPayerId: number;
  year: number;
  month: number;
  debit10: number;
  debit5: number;
  taxable10Emitted: number;
  taxable5Emitted: number;
  exemptEmitted: number;
  credit10: number;
  credit5: number;
  taxable10Received: number;
  taxable5Received: number;
  exemptReceived: number;
  netIvaPayable: number;
}

export interface IvaPeriodSummaryRow {
  id: number;
  year: number;
  month: number;
  debit10: number;
  debit5: number;
  credit10: number;
  credit5: number;
  netIvaPayable: number;
  consolidatedAt: string;
}

export interface IvaOverview {
  current: IvaPeriodResult;
  previous: IvaPeriodResult;
  history: IvaPeriodSummaryRow[];
}

export interface IvaItem {
  itemId: number;
  description: string;
  iva10Percent: number;
  iva5Percent: number;
  taxed10Percent: number;
  taxed5Percent: number;
  amountExempt: number;
  itemOverride: boolean | null;
}

export interface IvaEmittedDoc {
  documentType: IvaDocumentType;
  documentId: number;
  documentNumber: string | null;
  counterparty: string | null;
  documentDate: string;
  currency: string;
  pygExchangeRate: number | null;
  iva10Percent: number;
  iva5Percent: number;
  docOverride: boolean | null;
  items: IvaItem[];
}

export interface IvaReceivedDoc {
  documentType: "received_document";
  documentId: number;
  receivedType: string;
  documentNumber: string;
  counterparty: string | null;
  documentDate: string;
  currency: string;
  pygExchangeRate: number | null;
  iva10Percent: number;
  iva5Percent: number;
  docOverride: boolean | null;
}

export type IvaSection =
  | "invoices"
  | "credit_notes"
  | "debit_notes"
  | "received";

export interface IvaSectionPage<T> {
  year: number;
  month: number;
  consolidated: boolean;
  page: number;
  pageSize: number;
  total: number;
  items: T[];
}

const json = async (resp: Response): Promise<any> => {
  try {
    return await resp.json();
  } catch {
    return null;
  }
};

export const getIvaOverview = async (
  taxPayerId: number,
): Promise<IvaOverview | null> => {
  const url = getAbsoluteUrl(`/iva/overview/?tax_payer_id=${taxPayerId}`);
  const resp = await fetchAuthenticated(url, {
    headers: { Accept: "application/json" },
  });
  const body = await json(resp);
  return body?.status === "success" ? (body.data as IvaOverview) : null;
};

export const getIvaPeriodSection = async <T>(
  taxPayerId: number,
  year: number,
  month: number,
  section: IvaSection,
  page: number,
  pageSize: number,
): Promise<IvaSectionPage<T> | null> => {
  const url = getAbsoluteUrl(
    `/iva/period/${year}/${month}/${section}/?tax_payer_id=${taxPayerId}&page=${page}&page_size=${pageSize}`,
  );
  const resp = await fetchAuthenticated(url, {
    headers: { Accept: "application/json" },
  });
  const body = await json(resp);
  return body?.status === "success" ? (body.data as IvaSectionPage<T>) : null;
};

export const setIvaInclusion = async (
  taxPayerId: number,
  documentType: IvaDocumentType,
  documentId: number,
  itemId: number | null,
  included: boolean,
): Promise<boolean> => {
  const url = getAbsoluteUrl(`/iva/inclusions/`);
  const resp = await fetchAuthenticated(url, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      taxPayerId,
      documentType,
      documentId,
      itemId,
      included,
    }),
  });
  const body = await json(resp);
  return body?.status === "success";
};
